import React, { useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import ConfirmModal from "../../../components/confirmModal/confirmModel";
import CustomToggleButton from "../../../components/customToggleButton/customToggleButton";
import {
  fetchPublicTableSheet,
  fetchTableBookingLayout,
  fetchTableSheetShareLink,
} from "../../../api/tableBookingApi";
import {
  flattenLayoutPricingRows,
  formatEffectivePriceHint,
  indexAreaPricingRows,
  normalizeManualAvailability,
  pickEventTableBookingFromItem,
  resolveEffectiveEventPricing,
  resolveEventChannelSoldOut,
  toOptionalPartySize,
  MANUAL_AVAILABILITY_VALUES,
} from "../../../helpers/eventTableBooking";
import {
  indexLiveSheetTables,
  resolveDisplayedManualAvailability,
} from "../../../helpers/eventTableBookingLiveStatus";
import {
  unwrapTableSheetResponse,
  unwrapTableSheetShareLink,
} from "../../../helpers/tableSheet";
import {
  manualAvailabilityButtonLabel,
  normalizeLayoutDetail,
} from "../../../pages/tableBooking/tableBookingUtils";

import "../currentEventTab/createEditEvent/eventTableBooking.css";
import "./eventTableBookingDetailView.css";

const LIVE_POLL_MS = 30000;

const EventTableBookingDetailView = ({
  eventItem,
  clubId,
  canManageToggles = false,
  onTableAvailabilityChange,
  onTableChannelSoldOutChange,
  toggleSavingKey = "",
}) => {
  const { t } = useTranslation();
  const booking = useMemo(
    () => pickEventTableBookingFromItem(eventItem),
    [eventItem]
  );
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [expandedZoneIds, setExpandedZoneIds] = useState(() => new Set());
  const [pendingAvailabilityChange, setPendingAvailabilityChange] = useState(null);
  const [pendingChannelChange, setPendingChannelChange] = useState(null);
  const [liveIndex, setLiveIndex] = useState(null);

  const resolvedClubId =
    clubId != null && clubId !== ""
      ? String(clubId)
      : String(eventItem?.clubid ?? eventItem?.clubId ?? "").trim() || null;

  const resolvedEventId = String(
    eventItem?._id ?? eventItem?.id ?? eventItem?.eventId ?? ""
  ).trim();

  useEffect(() => {
    if (!booking.tableBookingEnabled || !resolvedClubId) {
      setLayout(null);
      return;
    }
    const layoutId = String(booking.tableBookingLayoutId ?? "").trim();
    if (!layoutId) {
      setLayout(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError("");
    fetchTableBookingLayout(resolvedClubId, layoutId)
      .then((res) => {
        if (cancelled) return;
        setLayout(normalizeLayoutDetail(res) || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setLayout(null);
        setLoadError(
          typeof err === "string"
            ? err
            : err?.message || t("TABLE_BOOKING_LAYOUTS_LOAD_ERROR")
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    booking.tableBookingEnabled,
    booking.tableBookingLayoutId,
    resolvedClubId,
    t,
  ]);

  // Drive Manual availability highlight from live table sheet bookings.
  useEffect(() => {
    if (!booking.tableBookingEnabled || !resolvedClubId || !resolvedEventId) {
      setLiveIndex(null);
      return;
    }

    let cancelled = false;
    let intervalId = null;

    const loadLive = async () => {
      try {
        const shareRaw = await fetchTableSheetShareLink(resolvedClubId);
        if (cancelled) return;
        const share = unwrapTableSheetShareLink(shareRaw);
        const slug = String(share?.slug || "").trim().toLowerCase();
        if (!slug) {
          if (!cancelled) setLiveIndex(null);
          return;
        }

        const sheetRaw = await fetchPublicTableSheet(slug, {
          eventId: resolvedEventId,
        });
        if (cancelled) return;
        const payload = unwrapTableSheetResponse(sheetRaw);
        setLiveIndex(indexLiveSheetTables(payload?.sheet || null));
      } catch {
        if (!cancelled) setLiveIndex(null);
      }
    };

    loadLive();
    intervalId = setInterval(loadLive, LIVE_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") loadLive();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [booking.tableBookingEnabled, resolvedClubId, resolvedEventId]);

  const overrideIndex = useMemo(
    () => indexAreaPricingRows(booking.tableBookingAreaPricing),
    [booking.tableBookingAreaPricing]
  );

  const pricingByZone = useMemo(() => {
    const rows = flattenLayoutPricingRows(layout);
    const zones = rows.filter((r) => r.kind === "zone");
    const tables = rows.filter((r) => r.kind === "table");
    return zones.map((zone) => ({
      ...zone,
      tables: tables.filter((table) => table.areaId === zone.areaId),
    }));
  }, [layout]);

  const layoutKey = String(booking.tableBookingLayoutId ?? "").trim();

  useEffect(() => {
    setExpandedZoneIds(new Set());
  }, [layoutKey]);

  const toggleZoneExpand = (areaId) => {
    setExpandedZoneIds((current) => {
      const next = new Set(current);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  if (!booking.tableBookingEnabled) return null;

  const resolveRowMeta = (row, { nestedTables = null } = {}) => {
    const key = row.tableId
      ? `${row.areaId}:${row.tableId}`
      : row.areaId;
    const override = overrideIndex.get(key);
    const layoutDefault = row.layoutDefault || {};
    const effective = resolveEffectiveEventPricing(row, overrideIndex);
    const minParty =
      toOptionalPartySize(override?.minPartySize) ??
      layoutDefault.minPartySize ??
      null;
    const maxParty =
      toOptionalPartySize(override?.maxPartySize) ??
      layoutDefault.maxPartySize ??
      null;
    const savedAvailability = normalizeManualAvailability(
      override?.manualAvailability ?? layoutDefault.manualAvailability
    );
    const availability = resolveDisplayedManualAvailability(
      row,
      liveIndex,
      savedAvailability,
      nestedTables
    );

    return {
      price: formatEffectivePriceHint(effective, layoutDefault),
      minParty: minParty != null ? minParty : "—",
      maxParty: maxParty != null ? maxParty : "—",
      availability,
      savedAvailability,
      availabilityLabel: manualAvailabilityButtonLabel(availability, t),
      layoutPrice: layoutDefault.price ?? 0,
      currency: layoutDefault.currency || "INR",
      onlineSoldOut: resolveEventChannelSoldOut(
        row,
        overrideIndex,
        "onlineSoldOut"
      ),
      offlineSoldOut: resolveEventChannelSoldOut(
        row,
        overrideIndex,
        "offlineSoldOut"
      ),
    };
  };

  const requestAvailabilityChange = (row, meta, nextAvailability) => {
    if (meta.savedAvailability === nextAvailability) return;
    setPendingAvailabilityChange({
      areaId: row.areaId,
      tableId: row.tableId,
      manualAvailability: nextAvailability,
      layoutPrice: meta.layoutPrice,
      currency: meta.currency,
      rowLabel: row.label,
    });
  };

  const closeAvailabilityConfirm = () => setPendingAvailabilityChange(null);

  const confirmAvailabilityChange = () => {
    if (!pendingAvailabilityChange) return;
    onTableAvailabilityChange?.({
      areaId: pendingAvailabilityChange.areaId,
      tableId: pendingAvailabilityChange.tableId,
      manualAvailability: pendingAvailabilityChange.manualAvailability,
      layoutPrice: pendingAvailabilityChange.layoutPrice,
      currency: pendingAvailabilityChange.currency,
    });
    setPendingAvailabilityChange(null);
  };

  const requestChannelSoldOutChange = (row, meta, field, checked, nestedTables) => {
    const current = meta[field]?.checked;
    if (Boolean(current) === Boolean(checked)) return;
    setPendingChannelChange({
      areaId: row.areaId,
      tableId: row.tableId,
      field,
      checked: Boolean(checked),
      layoutPrice: meta.layoutPrice,
      currency: meta.currency,
      nestedTables: nestedTables || [],
    });
  };

  const closeChannelConfirm = () => setPendingChannelChange(null);

  const confirmChannelChange = () => {
    if (!pendingChannelChange) return;
    onTableChannelSoldOutChange?.({
      areaId: pendingChannelChange.areaId,
      tableId: pendingChannelChange.tableId,
      layoutPrice: pendingChannelChange.layoutPrice,
      currency: pendingChannelChange.currency,
      nestedTables: pendingChannelChange.nestedTables,
      ...(pendingChannelChange.field === "offlineSoldOut"
        ? { offlineSoldOut: pendingChannelChange.checked }
        : { onlineSoldOut: pendingChannelChange.checked }),
    });
    setPendingChannelChange(null);
  };

  const renderChannelSoldOutCell = (row, meta, field, nestedTables) => {
    const channel = meta[field] || { checked: false, inherited: false, source: "layout" };
    const inheritedFromZone =
      Boolean(row.tableId) &&
      channel.inherited &&
      channel.source === "zone";
    if (!canManageToggles) {
      return channel.checked
        ? t("TABLE_BOOKING_TOGGLE_ON")
        : t("TABLE_BOOKING_TOGGLE_OFF");
    }
    const savingKey = `table:${row.areaId}:${row.tableId || "zone"}:channelSoldOut`;
    const isSaving = String(toggleSavingKey || "") === savingKey;
    return (
      <div className="etb-detail-channel-toggle">
        <CustomToggleButton
          id={`etb-detail-${field}-${row.areaId}-${row.tableId || "zone"}`}
          checked={Boolean(channel.checked)}
          disabled={isSaving || inheritedFromZone}
          onCheckChange={(checked) =>
            requestChannelSoldOutChange(row, meta, field, checked, nestedTables)
          }
        />
        {inheritedFromZone ? (
          <span className="etb-detail-inherited">
            {t("TABLE_BOOKING_CHANNEL_SOLD_OUT_INHERITED")}
          </span>
        ) : null}
      </div>
    );
  };

  const renderAvailabilityCell = (row, meta) => {
    if (!canManageToggles) {
      return meta.availabilityLabel;
    }
    const savingKey = `table:${row.areaId}:${row.tableId || "zone"}:`;
    const isSaving = String(toggleSavingKey || "").startsWith(savingKey);
    return (
      <div className="etb-avail-group etb-detail-avail-group">
        {MANUAL_AVAILABILITY_VALUES.map((k) => (
          <button
            key={k}
            type="button"
            className={`etb-avail-btn is-${k.toLowerCase()} ${
              meta.availability === k ? "is-active" : ""
            }`}
            disabled={isSaving}
            onClick={() => requestAvailabilityChange(row, meta, k)}
          >
            {manualAvailabilityButtonLabel(k, t)}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="event-table-booking-detail-view etb-root">
      <div className="etb-detail-header">
        <h5 className="etb-detail-title mb-1">
          {t("EVENT_TABLE_BOOKING_TITLE")}
        </h5>
        <p className="etb-detail-sub mb-0">
          {t("EVENT_TABLE_BOOKING_DETAIL_VIEW_HELP")}
        </p>
      </div>

      <div className="etb-detail-meta">
        <div>
          <span className="etb-detail-label">
            {t("EVENT_TABLE_BOOKING_LAYOUT")}
          </span>
          <div className="etb-detail-value">
            {layout?.title || booking.tableBookingLayoutId || "—"}
            {booking.tableBookingViewkey
              ? ` (${booking.tableBookingViewkey})`
              : ""}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="etb-loading mb-0">{t("EVENT_TABLE_BOOKING_LOADING")}</p>
      ) : null}

      {loadError ? (
        <Alert variant="warning" className="py-2 small etb-alert">
          {loadError}
        </Alert>
      ) : null}

      {!loading && !loadError && pricingByZone.length > 0 ? (
        <div className="typePaymentTableStyle etb-detail-table-wrap">
          <div className="viewTableStyle">
            <div className="tableHeadRowStyle etb-detail-table-head">
              <div className="headTitleStyle">
                {t("EVENT_TABLE_BOOKING_COL_AREA")}
              </div>
              <div className="headTitleStyle">
                {t("EVENT_TABLE_BOOKING_COL_EVENT_PRICE")}
              </div>
              <div className="headTitleStyle">
                {t("TABLE_BOOKING_MIN_PARTY")}
              </div>
              <div className="headTitleStyle">
                {t("TABLE_BOOKING_MAX_PARTY")}
              </div>
              <div className="headTitleStyle">
                {t("TABLE_BOOKING_MANUAL_AVAIL")}
              </div>
              <div className="headTitleStyle etb-detail-so-head">
                {t("TABLE_BOOKING_ONLINE_SOLD_OUT")}
              </div>
              <div className="headTitleStyle etb-detail-so-head">
                {t("TABLE_BOOKING_OFFLINE_SOLD_OUT")}
              </div>
            </div>
            <div className="scrollViewStyle">
              {pricingByZone.map((zone) => {
                const hasNestedTables = zone.tables.length > 0;
                const zoneMeta = resolveRowMeta(zone, {
                  nestedTables: hasNestedTables ? zone.tables : null,
                });
                const isExpanded = expandedZoneIds.has(zone.areaId);

                return (
                  <React.Fragment key={zone.areaId}>
                    <div
                      className={`tbodyRowStyle etb-detail-zone-row ${
                        hasNestedTables ? "etb-detail-zone-row--expandable" : ""
                      }`}
                    >
                      <div className="tbodyTitleStyle etb-detail-zone-name">
                        {hasNestedTables ? (
                          <button
                            type="button"
                            className="etb-detail-zone-toggle"
                            onClick={() => toggleZoneExpand(zone.areaId)}
                            aria-expanded={isExpanded}
                          >
                            <span className="etb-detail-zone-chevron" aria-hidden="true">
                              <i
                                className={`mdi ${
                                  isExpanded ? "mdi-chevron-down" : "mdi-chevron-right"
                                }`}
                              />
                            </span>
                            <span className="etb-detail-zone-toggle-main">
                              <span className="etb-detail-zone-label">{zone.label}</span>
                              {!isExpanded ? (
                                <span className="etb-detail-table-count">
                                  {t("EVENT_TABLE_BOOKING_NESTED_TABLE_COUNT", {
                                    count: zone.tables.length,
                                  })}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        ) : (
                          zone.label
                        )}
                      </div>
                      <div className="tbodyTitleStyle">{zoneMeta.price}</div>
                      <div className="tbodyTitleStyle">{zoneMeta.minParty}</div>
                      <div className="tbodyTitleStyle">{zoneMeta.maxParty}</div>
                      <div className="tbodyTitleStyle">
                        {renderAvailabilityCell(zone, zoneMeta)}
                      </div>
                      <div className="tbodyTitleStyle">
                        {renderChannelSoldOutCell(
                          zone,
                          zoneMeta,
                          "onlineSoldOut",
                          zone.tables
                        )}
                      </div>
                      <div className="tbodyTitleStyle">
                        {renderChannelSoldOutCell(
                          zone,
                          zoneMeta,
                          "offlineSoldOut",
                          zone.tables
                        )}
                      </div>
                    </div>
                    {hasNestedTables && isExpanded
                      ? zone.tables.map((table) => {
                          const tableMeta = resolveRowMeta(table);
                          return (
                            <div
                              className="tbodyRowStyle etb-detail-table-row"
                              key={`${table.areaId}:${table.tableId}`}
                            >
                              <div className="tbodyTitleStyle">
                                <span className="etb-detail-table-indent">
                                  {table.label}
                                </span>
                                <span className="etb-detail-table-sub">
                                  {t("EVENT_TABLE_BOOKING_NESTED_TABLE")}
                                </span>
                              </div>
                              <div className="tbodyTitleStyle">
                                {tableMeta.price}
                              </div>
                              <div className="tbodyTitleStyle">
                                {tableMeta.minParty}
                              </div>
                              <div className="tbodyTitleStyle">
                                {tableMeta.maxParty}
                              </div>
                              <div className="tbodyTitleStyle">
                                {renderAvailabilityCell(table, tableMeta)}
                              </div>
                              <div className="tbodyTitleStyle">
                                {renderChannelSoldOutCell(
                                  table,
                                  tableMeta,
                                  "onlineSoldOut"
                                )}
                              </div>
                              <div className="tbodyTitleStyle">
                                {renderChannelSoldOutCell(
                                  table,
                                  tableMeta,
                                  "offlineSoldOut"
                                )}
                              </div>
                            </div>
                          );
                        })
                      : null}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      ) : !loading && !loadError && layout ? (
        <Alert variant="warning" className="py-2 small etb-alert mb-0">
          {t("EVENT_TABLE_BOOKING_NO_AREAS")}
        </Alert>
      ) : null}

      <ConfirmModal
        modalTitle={t("EVENT_TABLE_BOOKING_CONFIRM_STATUS_TITLE")}
        modalDescription={t("EVENT_TABLE_BOOKING_CONFIRM_STATUS_CHANGE")}
        isUnderline={false}
        showModal={Boolean(pendingAvailabilityChange)}
        leftBtnTitle={t("YES_CONFIRM")}
        isModelIcon={true}
        iconColor="mdi mdi-help-circle-outline"
        iconCricle="iconCricleCancel"
        leftBtnClass="ok_Btn"
        isFirstButton={true}
        isSecondButton={true}
        rightBtnClass="borderButton"
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={confirmAvailabilityChange}
        rightButtonClick={closeAvailabilityConfirm}
        onHide={closeAvailabilityConfirm}
      />
      <ConfirmModal
        modalTitle={t("TABLE_BOOKING_CONFIRM_CHANNEL_TITLE")}
        modalDescription={t("TABLE_BOOKING_CONFIRM_CHANNEL_CHANGE", {
          channel:
            pendingChannelChange?.field === "offlineSoldOut"
              ? t("TABLE_BOOKING_OFFLINE_SOLD_OUT")
              : t("TABLE_BOOKING_ONLINE_SOLD_OUT"),
          state: pendingChannelChange?.checked
            ? t("TABLE_BOOKING_STATE_ON")
            : t("TABLE_BOOKING_STATE_OFF"),
        })}
        isUnderline={false}
        showModal={Boolean(pendingChannelChange)}
        leftBtnTitle={t("YES_CONFIRM")}
        isModelIcon={true}
        iconColor="mdi mdi-help-circle-outline"
        iconCricle="iconCricleCancel"
        leftBtnClass="ok_Btn"
        isFirstButton={true}
        isSecondButton={true}
        rightBtnClass="borderButton"
        rightBtnTitle={t("NO_CANCEL")}
        onProcced={confirmChannelChange}
        rightButtonClick={closeChannelConfirm}
        onHide={closeChannelConfirm}
      />
    </div>
  );
};

export default EventTableBookingDetailView;
